'use client';

import React, { FormEvent, ChangeEvent, useState, useRef, useEffect } from 'react';
import { CustomCheckbox } from '../../components/forms/CustomCheckbox';

const animateCounter = (el: Element) => {
  const to = Number(el.getAttribute('data-to'));
  const duration = 900;
  const start = performance.now();
  const step = (now: number) => {
    const progress = Math.min(1, (now - start) / duration);
    (el as HTMLElement).textContent = Math.floor(to * progress).toString();
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
};

const Card: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <article className={`card rounded-2xl p-6 ${className || ''}`}>{children}</article>
);

const Chip: React.FC<React.PropsWithChildren<{ href: string }>> = ({ href, children }) => (
  <a href={href} className="px-3 py-1 rounded-full hover:bg-white/25 text-sm" style={{ background: 'rgba(255,255,255,.14)' }}>{children}</a>
);

export default function Home() {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', message: '', formName: 'Миссия' });
  const [isAccepted, setIsAccepted] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAccepted(e.target.checked);
    if (status && status.startsWith("❌")) {
      setStatus(null);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isAccepted) {
      setStatus("❌ Пожалуйста, согласитесь с обработкой персональных данных.");
      return;
    }

    setSubmitting(true);
    setStatus("Отправка данных...");

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/callback-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: {
            ...formData,
            formName: "Миссия", 
          }
        }),
      });

      if (response.ok) {
        setStatus("✅ Ваша заявка успешно отправлена!");
        setFormData({ name: '', email: '', company: '', phone: '', message: '', formName: 'Миссия' });
        setIsAccepted(false);
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || "Произошла неизвестная ошибка.";
        setStatus(`❌ Ошибка: ${errorMessage}`);
      }
    } catch (error) {
      setStatus("❌ Ошибка сети. Пожалуйста, попробуйте снова.");
      console.error("Ошибка:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const countersRef = useRef<NodeListOf<Element> | null>(null);
  useEffect(() => {
    countersRef.current = document.querySelectorAll('.counter');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    countersRef.current.forEach(counter => observer.observe(counter));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            <a href="#mission" className="chip px-3 py-1 rounded-full hover:bg-white/25">Суть миссии</a>
            <a href="#pillars" className="chip px-3 py-1 rounded-full hover:bg-white/25">Принципы</a>
            <a href="#promise" className="chip px-3 py-1 rounded-full hover:bg-white/25">Обещание клиентам</a>
            <a href="#candidates" className="chip px-3 py-1 rounded-full hover:bg-white/25">Обещание кандидатам</a>
            <a href="#ethics" className="chip px-3 py-1 rounded-full hover:bg-white/25">Этика и данные</a>
            <a href="#impact" className="chip px-3 py-1 rounded-full hover:bg-white/25">Влияние и цели</a>
            <a href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">FAQ</a>
            <a href="#contact" className="chip px-3 py-1 rounded-full hover:bg-white/25">Связаться</a>
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Наша миссия — ускорять справедливый найм</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Мы делаем найм предсказуемым для бизнеса и честным для людей: прозрачные SLA, измеримые результаты и уважение к кандидату.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#promise" className="btn btn-ghost">Что обещаем клиентам</a>
            <a href="#impact" className="btn btn-accent">Наши цели и метрики</a>
          </div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="mission" className="grid gap-6 md:grid-cols-3">
          <Card className="md:col-span-2">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Суть миссии</h2>
            <p className="text-gray-200">
              Помогаем компаниям быстро закрывать нужные роли, а кандидатам — находить работу, где их ценят.
              Мы отвечаем за сроки, качество и коммуникацию, чтобы каждая сторона выигрывала.
            </p>
            <ul className="mt-4 grid gap-3 md:grid-cols-2 text-sm text-gray-200">
              <li className="card rounded-xl p-4">• Соглашение об уровне сервиса (SLA) на каждый этап: от брифа до оффера</li>
              <li className="card rounded-xl p-4">• Метрики CPH/LTV/явка в BI-дашбордах</li>
              <li className="card rounded-xl p-4">• Обучение менеджеров по найму и команды</li>
              <li className="card rounded-xl p-4">• Честная обратная связь для каждого кандидата</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-semibold mb-2">Кому мы помогаем</h3>
            <p className="text-sm text-gray-200">IT-сфера, интернет, строительство и ремонт, транспорт и логистика, сфера обслуживания, уход и здоровье, администрация.</p>
            <hr className="my-4 border-white/10" />
            <h3 className="text-xl font-semibold mb-2">Как измеряем</h3>
            <p className="text-sm text-gray-200">SLA по срокам, качество найма, стоимость найма, явка и удержание.</p>
          </Card>
        </section>

        <section id="pillars" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">4 принципа работы</h2>
          <div className="grid gap-6 md:grid-cols-4">
            <Card><h3 className="text-xl font-semibold">Прозрачность</h3><p className="text-sm text-gray-200 mt-1">Еженедельные отчёты, доступ к статусам и данным.</p></Card>
            <Card><h3 className="text-xl font-semibold">Скорость</h3><p className="text-sm text-gray-200 mt-1">Поток кандидатов и быстрые слоты интервью.</p></Card>
            <Card><h3 className="text-xl font-semibold">Качество</h3><p className="text-sm text-gray-200 mt-1">Оценка по профилю роли и обратная связь для роста.</p></Card>
            <Card><h3 className="text-xl font-semibold">Забота</h3><p className="text-sm text-gray-200 mt-1">Этика, уважение и равные возможности.</p></Card>
          </div>
        </section>

        <section id="promise" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Обещание клиентам</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <span className="block px-4 py-1 rounded-full text-sm mb-2" style={{ background: 'rgba(255,255,255,0.12)' }}>Соглашение об уровне сервиса (SLA)</span>
              <h3 className="text-xl font-semibold">Предсказуемые сроки</h3>
              <p className="text-sm text-gray-200 mt-1">Согласованные сроки и план закрытия. Финансовая ответственность за срыв.</p>
            </Card>
            <Card>
              <span className="block px-4 py-1 rounded-full text-sm mb-2" style={{ background: 'rgba(255,255,255,0.12)' }}>Бизнес-аналитика (BI)</span>
              <h3 className="text-xl font-semibold">Числа, не ощущения</h3>
              <p className="text-sm text-gray-200 mt-1">Единые метрики: CPH, явка, этапы воронки, качество найма.</p>
            </Card>
            <Card>
              <span className="block px-4 py-1 rounded-full text-sm mb-2" style={{ background: 'rgba(255,255,255,0.12)' }}>Операционные процессы (Ops)</span>
              <h3 className="text-xl font-semibold">Сильная операционка</h3>
              <p className="text-sm text-gray-200 mt-1">Регулярные синки, контроль качества, план рисков и замены.</p>
            </Card>
          </div>
        </section>

        <section id="candidates" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Обещание кандидатам</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <Card><h3 className="text-xl font-semibold">Честная обратная связь</h3><p className="text-sm text-gray-200 mt-1">Понимание причин решения и рекомендации по развитию.</p></Card>
            <Card><h3 className="text-xl font-semibold">Прозрачный процесс</h3><p className="text-sm text-gray-200 mt-1">Статус заявки и следующий шаг всегда под рукой.</p></Card>
            <Card><h3 className="text-xl font-semibold">Равные возможности</h3><p className="text-sm text-gray-200 mt-1">Без дискриминации — только навыки, потенциал и ценности.</p></Card>
          </div>
        </section>

        <section id="ethics" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Этика, безопасность и данные</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <h3 className="text-xl font-semibold">Конфиденциальность</h3>
              <ul className="text-sm text-gray-200 mt-2 space-y-2">
                <li>• Соблюдаем требования персональных данных (152-ФЗ)</li>
                <li>• Доступ по роли, безопасное хранение и удаление по запросу</li>
                <li>• Защита каналов связи и логирование действий</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-semibold">Этичный подбор</h3>
              <ul className="text-sm text-gray-200 mt-2 space-y-2">
                <li>• Никаких закрытых «чёрных списков» и дискриминации</li>
                <li>• Проверяем источники и корректность вакансий</li>
                <li>• Уважение времени — слоты, дедлайны, обратная связь</li>
              </ul>
            </Card>
          </div>
        </section>

        <section id="impact" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Влияние и цели</h2>
          <div className="grid gap-6 md:grid-cols-4">
            <div className="card rounded-2xl p-6 text-center"><div className="text-5xl font-extrabold counter" data-to="96">0</div><div className="text-xs text-gray-300 mt-1">средняя явка, %</div></div>
            <div className="card rounded-2xl p-6 text-center"><div className="text-5xl font-extrabold counter" data-to="19">0</div><div className="text-xs text-gray-300 mt-1">Снижение стоимости найма</div></div>
            <div className="card rounded-2xl p-6 text-center"><div className="text-5xl font-extrabold counter" data-to="24">0</div><div className="text-xs text-gray-300 mt-1">SLA на замену, ч</div></div>
            <div className="card rounded-2xl p-6 text-center"><div className="text-5xl font-extrabold counter" data-to="180">0</div><div className="text-xs text-gray-300 mt-1">закрытий за квартал</div></div>
          </div>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <Card>
              <h3 className="text-xl font-semibold mb-3">Инициативы на год</h3>
              <div className="text-sm">Качество офферов — 90% принятия</div>
              <div className="relative h-2.5 rounded-full mb-3" style={{ backgroundColor: 'rgba(255,255,255,.2)' }}>
                <span className="absolute top-0 left-0 h-full rounded-full" style={{ width: '72%', backgroundColor: '#FFC300' }}></span>
              </div>
              <div className="text-sm">Скорость закрытия — −20% сроков</div>
              <div className="relative h-2.5 rounded-full mb-3" style={{ backgroundColor: 'rgba(255,255,255,.2)' }}>
                <span className="absolute top-0 left-0 h-full rounded-full" style={{ width: '45%', backgroundColor: '#FFC300' }}></span>
              </div>
              <div className="text-sm">Обратная связь кандидатам — 100%</div>
              <div className="relative h-2.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,.2)' }}>
                <span className="absolute top-0 left-0 h-full rounded-full" style={{ width: '84%', backgroundColor: '#FFC300' }}></span>
              </div>
            </Card>
            <Card>
              <h3 className="text-xl font-semibold mb-3">Как принимаем решения</h3>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Цель → метрика → эксперимент → ретро</li>
                <li>• На стороне кандидата: качество опыта важнее объёма</li>
                <li>• На стороне бизнеса: прозрачность стоимости и сроков</li>
              </ul>
            </Card>
          </div>
        </section>

        <section id="faq" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">FAQ</h2>
          <div className="space-y-3">
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Как контролируете качество?</summary>
              <p className="mt-2 text-sm text-gray-200">Единые критерии оценки, двойной просмотр резюме, стендапы и ревью интервью.</p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Что происходит при срыве сроков?</summary>
              <p className="mt-2 text-sm text-gray-200">Финансовая ответственность и корректирующий план с дедлайнами.</p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Можно ли интегрироваться с нашей ATS?</summary>
              <p className="mt-2 text-sm text-gray-200">Да, работаем с популярными системами управления подбором персонала (ATS) и системами управления человеческими ресурсами (HRIS) и выгружаем отчёты в системы бизнес-аналитики (BI).</p>
            </details>
          </div>
        </section>

        <section id="contact" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Связаться с нами</h2>
          <form className="simple-card grid gap-4 md:grid-cols-2" onSubmit={handleSubmit}>
            <input name="name" value={formData.name} onChange={handleChange} className="rounded-xl px-4 py-3 text-gray-900" placeholder="Имя*" required />
            <input name="email" value={formData.email} onChange={handleChange} className="rounded-xl px-4 py-3 text-gray-900" placeholder="E-mail*" type="email" required />
            <input name="company" value={formData.company} onChange={handleChange} className="rounded-xl px-4 py-3 text-gray-900" placeholder="Компания" />
            <input name="phone" value={formData.phone} onChange={handleChange} className="rounded-xl px-4 py-3 text-gray-900" placeholder="Телефон" />
            <textarea name="message" value={formData.message} onChange={handleChange} className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2" rows={4} placeholder="Коротко опишите задачу"></textarea>

            <div className="md:col-span-2 mt-2">
              <CustomCheckbox
                name="data-agreement"
                isChecked={isAccepted}
                onChange={handleCheckboxChange}
                textColorClass="text-blue-200"
                linkColorClass="text-yellow-300"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn btn-accent md:col-span-2 disabled:opacity-50"
            >
              {submitting ? "Отправка..." : "Отправить"}
            </button>

            {status && (
              <div className={`mt-2 text-center md:col-span-2 text-sm ${status.startsWith("❌") ? "text-orange-400" : "text-green-300"}`}>
                {status}
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}